import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { Box, Typography, Button, Chip, IconButton } from '@mui/material'
import UserLayout from '../../layouts/UserLayout'
import ImportFreshStockModal from '../../components/modals/ImportFreshStockModal'
import { DOMAIN } from '../../utils/api'
import PickDateWithoutShiftGrow from '../../components/PickDateWithoutShiftGrow'
import WbSunnyIcon from '@mui/icons-material/WbSunny'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import { mainGreen } from '../../utils/color'
import CheckFreshStockModal from '../../components/modals/CheckFreshStockModal'
import axios from 'axios'
import { getFromStorage } from '../../utils/storage'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { getTextOfInternalSale } from '../../utils/helper'

function ProductStockManagement() {
	const { currentUser } = useSelector((state) => state.currentUser)
	const { type } = useParams()
	const { state } = useLocation()
	const productId = state?.productId
	const [filteredData, setFilteredData] = useState([])
	const [stock, setStock] = useState(null)
	const [openImportModal, setOpenImportModal] = useState(false)
	const [openCheckModal, setOpenCheckModal] = useState(false)
	const today = new Date()
	const [timePick, setTimePick] = useState({
		date: today.getDate()?.toString(),
		month: (today.getMonth() + 1)?.toString(),
		year: today.getFullYear()?.toString(),
	})

	// Mặc định active ca sáng
	const [shift, setShift] = useState(true)

	const token = getFromStorage('current-user')?.accessToken
	const navigate = useNavigate()

	const productNames = {
		vani: 'Vani',
		'banh-gion': 'Bánh giòn',
		cafe: 'Cafe',
		'cha-gio': 'Chả giò',
	}

	useEffect(() => {
		const fetchData = async () => {
			if (!currentUser?.store || !type || !productId) return

			try {
				const { date, month, year } = timePick

				// Fetch history data
				const historyResponse = await axios({
					url: `${DOMAIN}/tonkho/history?storeId=${currentUser.store}&productId=${productId}&shift=${shift}&date=${date}&month=${month}&year=${year}`,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'GET',
				})

				const filtered = historyResponse.data.filter((item) => {
					return (shift && item.shiftCode === 'S') || (!shift && item.shiftCode === 'C')
				})

				setFilteredData(filtered)

				// Fetch current stock data
				const stockResponse = await axios({
					url: `${DOMAIN}/tonkho?storeId=${currentUser.store}`,
					headers: {
						Authorization: `Bearer ${token}`,
					},
					method: 'GET',
				})

				// Find stock for current product
				const currentProduct = stockResponse.data.find((item) => {
					return String(item.productId) === String(productId)
				})
				setStock(currentProduct?.stock || 0)
			} catch (error) {
				console.error('Error fetching data:', error)
			}
		}

		fetchData()
	}, [currentUser?.store, type, shift, timePick, productId, token])

	// Handle time change
	const setTime = (newTime) => {
		setTimePick(newTime)
	}

	return (
		<UserLayout>
			<Box sx={{ maxWidth: '768px', margin: '0 auto', px: 2 }}>
				{/* Header */}
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						gap: 2,
						py: 1,
						background: '#fff',
						borderRadius: '8px',
						p: 2,
						mb: 2,
					}}>
					<IconButton onClick={() => navigate(-1)} sx={{ color: '#000' }}>
						<ArrowBackIcon />
					</IconButton>
					<Typography
						sx={{
							fontSize: '20px',
							fontWeight: 500,
							flex: 1,
						}}>
						Quản lý kho {productNames[type]}
					</Typography>
				</Box>

				{/* Tồn kho section */}
				<Box
					sx={{
						background: '#fff',
						borderRadius: '8px',
						p: 2,
						mb: 2,
					}}>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							mb: 1,
						}}>
						<Typography sx={{ fontSize: '16px', fontWeight: 500 }}>Tồn kho</Typography>
						<Typography
							component='span'
							sx={{
								color: mainGreen,
								fontSize: '16px',
								fontWeight: 700,
							}}>
							{stock} phần
						</Typography>
					</Box>

					<Box sx={{ display: 'flex', gap: 2 }}>
						<Button
							variant='outlined'
							sx={{
								borderRadius: 8,
								borderColor: '#4caf50',
								color: '#4caf50',
								flex: 1,
								'&:hover': {
									borderColor: '#4caf50',
									backgroundColor: 'rgba(76, 175, 80, 0.04)',
								},
							}}
							onClick={() => setOpenImportModal(true)}>
							Nhập kho {type}
						</Button>
						<Button
							variant='outlined'
							sx={{
								borderRadius: 8,
								borderColor: '#f44336',
								color: '#f44336',
								flex: 1,
								'&:hover': {
									borderColor: '#f44336',
									backgroundColor: 'rgba(244, 67, 54, 0.04)',
								},
							}}
							onClick={() => setOpenCheckModal(true)}>
							Kiểm kho {type}
						</Button>
					</Box>
				</Box>

				{/* Lịch sử section */}
				<Box
					sx={{
						background: '#fff',
						borderRadius: '8px',
						p: 2,
						mb: 2,
					}}>
					<Typography
						sx={{
							fontSize: '18px',
							textAlign: 'center',
							fontWeight: 500,
							mb: 2,
						}}>
						Lịch sử xuất và tái nhập
					</Typography>
					<PickDateWithoutShiftGrow time={timePick} setTime={setTime} />
					<Box display='flex' justifyContent='center'>
						<Chip
							onClick={() => {
								setShift(true)
							}}
							size='small'
							sx={{
								cursor: 'pointer',
								mr: 4,
								background: shift ? mainGreen : undefined,
								color: shift ? '#fff' : '#000',
								'&:hover': {
									background: shift ? mainGreen : undefined,
									color: shift ? '#fff' : '#000',
								},
							}}
							label={
								<Typography
									sx={{
										fontSize: '14px',
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										lineHeight: 1.3,
									}}>
									<WbSunnyIcon sx={{ mr: 1, fontSize: '20px' }} /> Sáng
								</Typography>
							}
						/>
						<Chip
							onClick={() => {
								setShift(false)
							}}
							size='small'
							sx={{
								cursor: 'pointer',
								background: !shift ? mainGreen : undefined,
								color: !shift ? '#fff' : '#000',
								'&:hover': {
									background: !shift ? mainGreen : undefined,
									color: !shift ? '#fff' : '#000',
								},
							}}
							label={
								<Typography
									sx={{
										fontSize: '14px',
										display: 'flex',
										justifyContent: 'center',
										alignItems: 'center',
										lineHeight: 1.3,
									}}>
									<DarkModeIcon sx={{ mr: 1, fontSize: '20px' }} /> Chiều
								</Typography>
							}
						/>
					</Box>
				</Box>

				{/* History List */}
				<Box
					sx={{
						background: '#fff',
						borderRadius: '8px',
						p: 2,
					}}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
						{filteredData.length > 0 ? (
							filteredData.map((item, index) => (
								<Box
									key={index}
									sx={{
										display: 'flex',
										justifyContent: 'space-between',
										alignItems: 'flex-start',
										borderBottom: '1px solid #eee',
										py: 1,
									}}>
									<Typography sx={{ flex: 1 }}>
										<b>{item.user?.name}</b>{' '}
										{item.action === 'soldProduct' ? (
											<span style={{ color: '#198754' }}>bán ra -{item.quantity} phần.</span>
										) : item.action === 'importStock' ? (
											<span style={{ color: '#198754' }}>nhập +{item.quantity} phần.</span>
										) : item.action === 'crossStoreSell' || item.action === 'crossStoreBuy' ? (
											getTextOfInternalSale(item, 'phần')
										) : item.action === 'checkStock' ? (
											<span style={{ color: '#0d6efd' }}>kiểm kho +{item.quantity} phần.</span>
										) : null}{' '}
										Tồn <span style={{ color: '#dc3545' }}>{item.afterQuantity} phần</span>
										{item.note && (
											<Typography
												component='p'
												sx={{
													fontStyle: 'italic',
													margin: 0,
													fontSize: '0.9em',
												}}>
												Ghi chú: {item.note}
											</Typography>
										)}
									</Typography>

									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'flex-end',
											gap: 0.5,
										}}>
										<Typography variant='caption' sx={{ color: 'text.secondary' }}>
											Ca {item.shiftCode === 'S' ? 'Sáng' : 'Chiều'} - {item.timeStamp}
										</Typography>
										<Box
											sx={{
												display: 'flex',
												gap: 0.5,
											}}>
											{item.images?.map((photo, photoIndex) => (
												<Box
													key={photoIndex}
													component='img'
													src={photo}
													sx={{
														width: 40,
														height: 40,
														objectFit: 'cover',
														borderRadius: 1,
													}}
												/>
											))}
										</Box>
									</Box>
								</Box>
							))
						) : (
							<Typography sx={{ color: '#000', fontWeight: 700, fontSize: '16px', textAlign: 'center', py: 2 }}>
								(Chưa có dữ liệu)
							</Typography>
						)}
					</Box>
				</Box>
			</Box>
			<ImportFreshStockModal
				open={openImportModal}
				onClose={() => setOpenImportModal(false)}
				type={type}
				productId={productId}
				storeId={currentUser.store}
			/>
			<CheckFreshStockModal
				open={openCheckModal}
				onClose={() => setOpenCheckModal(false)}
				type={type}
				productId={productId}
				storeId={currentUser.store}
			/>
		</UserLayout>
	)
}

export default ProductStockManagement
